/**
 * Sustained-throughput run controller.
 *
 * Drives ONE node's root-detection throughput loop: keeps `concurrency`
 * batch calls in flight and issues fresh batches until `durationMs`
 * elapses or `shouldStop()` fires. Each batch is a single
 * `pipelineExecutor.runPipelineBatch` call carrying `batchSize` copies of
 * the pinned frame; batch dispatch is INJECTED (`dispatchBatch`) so this
 * controller is fully unit-testable without a real cap, clock, or models.
 *
 * The controller is transport/clock-agnostic: the caller supplies `now`
 * (wall clock) and `shouldStop` (abort flag). This keeps the loop
 * deterministic under test and lets the server handler wire the real
 * `Date.now` + a per-session abort flag.
 */

/** Outcome of one batch dispatch — one detection count per frame in the batch. */
export interface BatchDispatchResult {
  /** Number of frames the batch returned results for (== batchSize on success). */
  readonly frameCount: number
  /** Detections per frame (length == frameCount). Used for det/s + sanity check. */
  readonly detectionCounts: readonly number[]
}

export interface SustainedRunOptions {
  /**
   * Issue ONE batch call and resolve when the node returns. Rejects on a
   * transport / inference error (counted, loop continues). The controller
   * never inspects the frame bytes — dispatch owns the pinned-frame batch.
   */
  readonly dispatchBatch: () => Promise<BatchDispatchResult>
  /** Max in-flight batch calls (input parallelization). Clamped to ≥ 1. */
  readonly concurrency: number
  /** Total run duration in ms (N seconds × 1000). */
  readonly durationMs: number
  /** Monotonic wall clock in ms (injected for test determinism). */
  readonly now: () => number
  /** Cooperative stop flag — polled before every batch; true ⇒ drain + finish. */
  readonly shouldStop: () => boolean
}

export interface SustainedRunRaw {
  /** Per-batch-call wall latency in ms (one entry per completed batch). */
  readonly callMsList: readonly number[]
  /** Total frames processed (Σ frameCount over completed batches). */
  readonly totalFrames: number
  /** Total completed batch calls. */
  readonly totalBatches: number
  /** Total detections emitted across all frames. */
  readonly totalDetections: number
  /** Number of frames whose detection count was recorded (== totalFrames). */
  readonly detectionSamples: number
  /** Batch calls that threw (transport / inference errors). */
  readonly errors: number
  /** Measured wall-clock window in ms. */
  readonly wallMs: number
  /** True when the run ended because `shouldStop()` fired (not the deadline). */
  readonly aborted: boolean
}

/**
 * Run the sustained loop for one node until the deadline or a stop.
 *
 * Concurrency model: `concurrency` worker loops share a single deadline.
 * Each worker repeatedly checks `!shouldStop() && now() < deadline`, then
 * issues one batch and awaits it before looping. Workers naturally
 * back-pressure on the node — the pool never queues more than
 * `concurrency` calls, matching the "input parallelization" knob.
 */
export async function runSustainedLoop(opts: SustainedRunOptions): Promise<SustainedRunRaw> {
  const concurrency = Math.max(1, Math.floor(opts.concurrency))
  const start = opts.now()
  const deadline = start + Math.max(0, opts.durationMs)

  const callMsList: number[] = []
  let totalFrames = 0
  let totalBatches = 0
  let totalDetections = 0
  let detectionSamples = 0
  let errors = 0

  const worker = async (): Promise<void> => {
    while (!opts.shouldStop() && opts.now() < deadline) {
      const t0 = opts.now()
      try {
        const out = await opts.dispatchBatch()
        const t1 = opts.now()
        callMsList.push(t1 - t0)
        totalBatches += 1
        totalFrames += out.frameCount
        for (const c of out.detectionCounts) {
          totalDetections += c
          detectionSamples += 1
        }
      } catch {
        errors += 1
      }
    }
  }

  await Promise.all(Array.from({ length: concurrency }, () => worker()))

  const wallMs = opts.now() - start
  return {
    callMsList,
    totalFrames,
    totalBatches,
    totalDetections,
    detectionSamples,
    errors,
    wallMs,
    aborted: opts.shouldStop(),
  }
}
